/**
 * Bikeshare API Module
 * Fetches real-time bike share station data from Toronto's public bike system.
 * Caches the status feed for a configurable duration (default: 3 minutes).
 *
 * Live availability is cross-referenced against bike-station-map.json, which
 * is a pre-computed proximity map generated at build time by the scraper.
 * station-ui.js calls getStationAvailability() for each station in the pool
 * returned by that map — this module handles caching so those calls only
 * trigger one network request.
 */

const BikeShareAPI = (() => {
    // ── Configuration ────────────────────────────────────────────────────────

    const CONFIG = {
        // Live station status (bikes available, docks available, etc.)
        STATUS_API_URL: 'https://tor.publicbikesystem.net/ube/gbfs/v1/en/station_status',

        // Cache duration for the status feed — no need to hammer the API
        CACHE_DURATION_MS: 3 * 60 * 1000, // 3 minutes
    };

    // ── Internal cache ───────────────────────────────────────────────────────

    let _statusCache = null;
    let _statusCacheTimestamp = null;

    console.log('[BikeShare API] Module initialized');

    // ── Cache helpers ────────────────────────────────────────────────────────

    /**
     * Returns true if the status cache exists and has not expired.
     * @returns {boolean}
     */
    function isCacheValid() {
        if (!_statusCache || !_statusCacheTimestamp) return false;
        return (Date.now() - _statusCacheTimestamp) < CONFIG.CACHE_DURATION_MS;
    }

    /**
     * Stores new status data in the in-memory cache.
     * @param {Object} data - Raw API response
     */
    function setCache(data) {
        _statusCache = data;
        _statusCacheTimestamp = Date.now();
        console.log('[BikeShare API] Status cache updated at', new Date(_statusCacheTimestamp).toLocaleTimeString());
    }

    // ── Core fetch ───────────────────────────────────────────────────────────

    /**
     * Fetch the full station status feed, using the cache when it's still fresh.
     * All other methods in this module call this internally, so only one network
     * request fires per cache window regardless of how many stations are queried.
     *
     * @returns {Promise<Object>} - Raw GBFS station_status response
     */
    async function fetchStationStatus() {
        if (isCacheValid()) {
            console.log('[BikeShare API] Using cached status data');
            return _statusCache;
        } else {
            console.log('[BikeShare API] NO cached status data');

        }

        console.log('[BikeShare API] Fetching fresh status data...');
        const response = await fetch(CONFIG.STATUS_API_URL);

        if (!response.ok) {
            throw new Error(`Station status API returned ${response.status}`);
        }

        const data = await response.json();

        if (!data.data || !Array.isArray(data.data.stations)) {
            throw new Error('Invalid API response — missing stations array');
        }

        setCache(data);
        return data;
    }

    // ── Public methods ───────────────────────────────────────────────────────

    /**
     * Get live availability for a single bike share station by ID.
     * Used by station-ui.js to enrich each station card in the drawer.
     *
     * Returns null (rather than throwing) when the station isn't found so that
     * the caller can handle the missing case gracefully without try/catch.
     *
     * @param {string} stationId - The bike share station ID (from bike-station-map.json)
     * @returns {Promise<Object|null>} - Availability data, or null if not found
     */
    async function getStationAvailability(stationId) {
        try {
            const data = await fetchStationStatus();
            const station = data.data.stations.find(s => s.station_id === stationId);

            if (!station) {
                console.warn(`[BikeShare API] Station ${stationId} not found in status feed`);
                return null;
            }

            station.lastReported = new Date(station.last_reported * 1000);

            return station;
        } catch (error) {
            console.error('[BikeShare API] getStationAvailability error:', error);
            return null;
        }
    }

    /**
     * Get aggregate totals across all active stations.
     * Useful for a system-wide status summary widget.
     *
     * @returns {Promise<Object|null>} - Totals object, or null on error
     */
    async function getTotalBikesAvailable() {
        try {
            const data = await fetchStationStatus();

            const totals = {
                totalBikes:        0,
                totalBikesDisabled: 0,
                totalDocks:        0,
                totalDocksDisabled: 0,
                totalMechanical:   0,
                totalEbikes:       0,
                activeStations:    0,
                lastUpdated:       new Date(),
            };

            data.data.stations.forEach(station => {
                if (station.status === 'IN_SERVICE' && station.is_installed) {
                    totals.totalBikes         += station.num_bikes_available;
                    totals.totalBikesDisabled += station.num_bikes_disabled;
                    totals.totalDocks         += station.num_docks_available;
                    totals.totalDocksDisabled += station.num_docks_disabled;
                    totals.totalMechanical    += station.num_bikes_available_types?.mechanical || 0;
                    totals.totalEbikes        += station.num_bikes_available_types?.ebike      || 0;
                    totals.activeStations     += 1;
                }
            });

            return totals;
        } catch (error) {
            console.error('[BikeShare API] getTotalBikesAvailable error:', error);
            return null;
        }
    }

    /**
     * Calculate straight-line distance between two coordinates (Haversine formula).
     *
     * @param {number} lat1
     * @param {number} lon1
     * @param {number} lat2
     * @param {number} lon2
     * @returns {number} - Distance in metres, rounded to nearest integer
     */
    function calculateDistance(lat1, lon1, lat2, lon2) {
        const R = 6371000; // Earth radius in metres
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
        return Math.round(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
    }

    /**
     * Find the closest stations with available bikes near a given coordinate.
     *
     * NOTE: station-ui.js no longer calls this directly — it uses
     * bike-station-map.json as the proximity pool and calls
     * getStationAvailability() per station instead, which is more efficient
     * because the proximity pool is already pre-sorted by distance.
     *
     * This method is retained for any other consumers that need a dynamic
     * nearest-available search without a pre-built map.
     *
     * @param {number} userLat
     * @param {number} userLon
     * @param {number} count        - Max stations to return (default: 2)
     * @param {Array}  stationInfo  - Optional array of static station objects
     *                                (name, address, etc.) keyed by station_id,
     *                                used to enrich the results.
     * @returns {Promise<Array>} - Closest available stations, sorted by distance
     */
    async function findClosestAvailableStations(userLat, userLon, count = 2, stationInfo = null) {
        try {
            const data = await fetchStationStatus();

            const available = data.data.stations.filter(s =>
                s.num_bikes_available > 0 &&
                s.status === 'IN_SERVICE' &&
                s.is_installed
            );

            if (available.length === 0) {
                console.warn('[BikeShare API] No available stations in status feed');
                return [];
            }

            return available
                .map(station => {
                    const info = stationInfo?.find(si => si.station_id === station.station_id) || {};
                    return {
                        stationId:      station.station_id,
                        name:           info.name    || `Station ${station.station_id}`,
                        address:        info.address || '',
                        lat:            station.lat,
                        lon:            station.lon,
                        distance_meters: calculateDistance(userLat, userLon, station.lat, station.lon),
                        bikesAvailable: station.num_bikes_available,
                        docksAvailable: station.num_docks_available,
                        isCharging:     station.is_charging_station,
                        bikeTypes:      station.num_bikes_available_types,
                    };
                })
                .sort((a, b) => a.distance_meters - b.distance_meters)
                .slice(0, count);
        } catch (error) {
            console.error('[BikeShare API] findClosestAvailableStations error:', error);
            return [];
        }
    }

    // ── Cache management ─────────────────────────────────────────────────────

    /** Manually invalidate the status cache. */
    function clearCache() {
        _statusCache = null;
        _statusCacheTimestamp = null;
        console.log('[BikeShare API] Cache cleared');
    }

    /**
     * Override the default cache TTL.
     * @param {number} durationMs - New TTL in milliseconds
     */
    function setCacheDuration(durationMs) {
        CONFIG.CACHE_DURATION_MS = durationMs;
        console.log(`[BikeShare API] Cache duration set to ${durationMs}ms`);
    }

    /**
     * How old the current cache is.
     * @returns {number|null} - Age in milliseconds, or null if no cache exists
     */
    function getCacheAge() {
        return _statusCacheTimestamp ? Date.now() - _statusCacheTimestamp : null;
    }

    // ── Public API ───────────────────────────────────────────────────────────

    return {
        // Core
        fetchStationStatus,
        getStationAvailability,
        getTotalBikesAvailable,
        // Search
        findClosestAvailableStations,
        calculateDistance,
        // Cache control
        clearCache,
        setCacheDuration,
        getCacheAge,
        isCacheValid,
    };
})();

// Make available globally for station-ui.js and any inline scripts
window.BikeShareAPI = BikeShareAPI;

// ES module export for any bundler / Node consumers
export default BikeShareAPI;